import { Box, Divider, Drawer, Typography } from '@mui/material';
import { ConfigManager } from '../../../../../application/config/ConfigManager';
import { ApplicationRoutes } from '../../../../../application/constants/routes';
import { ILocalization, ILocalizationToken } from '../../../../../application/language/ILocalization';
import { useI18n } from '../../../../../application/language/useI18n';
import { ContainerContext } from '../../../../../common/container/ContainerContext';
import { inject } from '../../../../../common/container/inject';
import { observer } from 'mobx-react';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { UserSystemRoleModelPermissionMap } from '../../../../../service/systemRole/entity/actions/UserSystemRoleModelPermissionMap';

import { Colors } from '../../../../design/color/Colors';
import { ListItemLink } from '../../../../design/list/ListItemLink';
import { IMainLayoutDomainStore, MainLayoutDomainStoreToken } from '../../store/domain/IMainLayoutDomainStore';
import { ILayoutUser } from '../../store/ui/ILayoutUser';
import { AdditionalNavItems } from './AdditionalNavItems';
import { LearningNavigation } from './LearningNavigation';
import { LeftNavigationInjects } from './LeftNavigationInjects';
import { MainLayoutBottomLogoMenu } from './parts/MainLayoutBottomLogoMenu';
import { NavigationHeader } from './parts/NavigationHeader';
import { ThemeSwitcher } from './parts/ThemeSwitcher';
import { useStyles } from './useStyles';

export interface ILeftNavigationProperties {
  layoutUser: ILayoutUser;
  layoutDomain: IMainLayoutDomainStore;
}

export const LeftNavigation = observer(({ layoutUser, layoutDomain }: ILeftNavigationProperties) => {
  const { getCurrentLanguage } = useI18n();
  const classes = useStyles();
  const context = useContext(ContainerContext);
  const MainLayoutBottomLogoMenuComponent = context.get(MainLayoutBottomLogoMenu);
  const licenseText = ConfigManager.getConfig().license.isSupport
    ? ConfigManager.getConfig().license.licenseString
    : '';
  const newMessagesCount = layoutUser.newMessagesCount;
  const isShowProjects = layoutUser.allowedPermissions.some((permission) => {
    return [
      UserSystemRoleModelPermissionMap['global-allow-any'],
      UserSystemRoleModelPermissionMap['is-have-access-to-projects'],
    ].includes(permission);
  });

  const isShowSettings = layoutUser.allowedPermissions.some((permission) => {
    return [
      UserSystemRoleModelPermissionMap['global-allow-any'],
      UserSystemRoleModelPermissionMap['settings-is-allow'],
    ].includes(permission);
  });

  useEffect(() => {
    layoutDomain.notificationDomain.lookForUpdates();
  }, [layoutDomain, layoutDomain.notificationDomain]);

  return (
    <Drawer
      className={classes.drawer}
      variant="permanent"
      classes={{
        paper: classes.drawerPaper,
      }}
      anchor="left"
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
        <NavigationHeader layoutUser={layoutUser} layoutDomain={layoutDomain} />

        <div className={classes.navContainer}>
          <NavigationMenu
            layoutDomain={layoutDomain}
            isShowProjects={isShowProjects}
            isShowSettings={isShowSettings}
            newMessages={newMessagesCount}
          />
        </div>

        <AdditionalNavItems layoutDomain={layoutDomain} />

        <Divider sx={{ borderColor: Colors.bgSurface, mx: '24px' }} />
        {/* <ThemeSwitcher /> */}

        <Box sx={{ marginBottom: 'auto' }}></Box>

        <Box sx={{ padding: '0px 0px 0px 0px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Typography
            style={{ fontSize: '9px', paddingLeft: '37px', paddingTop: '40px', whiteSpace: 'pre-line' }}
            variant="caption"
            gutterBottom
            component="div"
          >
            {licenseText}
          </Typography>
        </Box>

        <Divider color={Colors.bgSurface} />
        {/* TODO: запросить маленькие логотипы для подвала   */}
        {getCurrentLanguage() === 'ru' && <MainLayoutBottomLogoMenuComponent />}
      </Box>
    </Drawer>
  );
});

export const NavigationMenu = observer(({ isShowProjects, isShowSettings, layoutDomain }: any) => {
  const classes = useStyles();
  const [i18n] = useState(inject<ILocalization>(ILocalizationToken));
  const LeftNavigationExtensions = useContext(ContainerContext).get(LeftNavigationInjects);
  const extensionDomain = useContext(ContainerContext).getByToken(MainLayoutDomainStoreToken);
  const { pathname } = useLocation();
  const AnalyticsUrl = ApplicationRoutes.Analytics;
  const isProjectsSelected = pathname.startsWith('/project');
  const isAnalyticsSelected = pathname.startsWith(ApplicationRoutes.Analytics);
  const isSettingsSelected = pathname.startsWith('/settings');
  const isAdmin = layoutDomain.ui.activeUser.entity.allowedPermissions?.includes(
    UserSystemRoleModelPermissionMap['global-allow-any'],
  );

  return (
    <Fragment>
      {isShowProjects && <LearningNavigation layoutDomain={layoutDomain} />}

      {isShowProjects && (
        <ListItemLink
          itemKey="projects"
          url="/project/all"
          primaryText={i18n.translate('navigation.projects')}
          isSelected={isProjectsSelected}
        />
      )}

      {isShowProjects && isAdmin && (
        <ListItemLink
          itemKey={'analytics'}
          url={AnalyticsUrl}
          primaryText={i18n.translate('navigation.analytics')}
          isSelected={isAnalyticsSelected}
        />
      )}

      {isShowSettings && (
        <ListItemLink
          itemKey="settings"
          url="/settings"
          primaryText={i18n.translate('navigation.settings')}
          isSelected={isSettingsSelected}
        />
      )}

      {LeftNavigationExtensions.map((NavigationExtension) => {
        return <NavigationExtension key={NavigationExtension} layoutDomain={extensionDomain} />;
      })}

      <ListItemLink
        onClick={() => layoutDomain.clearActiveUser()}
        itemKey="logout"
        url="/logout"
        primaryText={i18n.translate('navigation.logout')}
        containerClassName={classes.logoutItem}
      />
    </Fragment>
  );
});
